import { io, Socket } from 'socket.io-client';
import type { UserStatus } from '@/types';
import type { Call } from '@/api/calls';

export interface ClientToServerEvents {
  'channel:join': (data: { channelId: string }) => void;
  'channel:leave': (data: { channelId: string }) => void;
  // Phase 7 — clientMessageId это идемпотентный ключ от клиента (UUIDv4).
  // Если задан, повторная отправка не создаёт дубликат.
  'message:send': (data: { channelId: string; content: string; type?: string; replyToId?: string; clientMessageId?: string; mediaFileId?: string }) => void;
  'dm:send': (data: { recipientId: string; content: string; clientMessageId?: string; replyToId?: string }) => void;
  'typing:start': (data: { channelId: string }) => void;
  'typing:stop': (data: { channelId: string }) => void;
  'dm:typing:start': (data: { recipientId: string }) => void;
  'dm:typing:stop': (data: { recipientId: string }) => void;
  'message:read': (data: { messageId: string }) => void;
  'status:set': (data: { status: UserStatus }) => void;

  // ── Calls ────────────────────────────────────────────────────────────────
  'call:start': (data: { calleeId: string; mode: 'VOICE' | 'VIDEO' }) => void;
  'call:accept': (data: { callId: string }) => void;
  'call:reject': (data: { callId: string }) => void;
  'call:cancel': (data: { callId: string }) => void;
  'call:hangup': (data: { callId: string; reason?: string }) => void;
  'call:offer': (data: { callId: string; sdp: string }) => void;
  'call:answer': (data: { callId: string; sdp: string }) => void;
  'call:ice-candidate': (data: { callId: string; candidate: unknown }) => void;
}

export interface ServerToClientEvents {
  'message:new': (message: unknown) => void;
  'message:edited': (message: unknown) => void;
  'message:deleted': (data: { id: string; channelId: string }) => void;
  'dm:new': (message: unknown) => void;
  // Phase 16 — DM delete / clear events.
  'dm:deleted': (data: { dmId: string; peerId: string; localOnly?: boolean }) => void;
  'dm:cleared': (data: { peerId: string; scope: 'me' | 'all' | 'peer-all' }) => void;
  'member:joined': (data: { channelId: string; member: unknown }) => void;
  'member:left': (data: { channelId: string; userId: string }) => void;
  'member:kicked': (data: { channelId: string; userId: string; reason?: string }) => void;
  'member:banned': (data: { channelId: string; userId: string; reason?: string }) => void;
  'typing:update': (data: { channelId: string; users: string[] }) => void;
  'dm:typing': (data: { fromUserId: string; isTyping: boolean }) => void;
  'user:status': (data: { userId: string; status: UserStatus }) => void;
  'notification:new': (notification: unknown) => void;
  'friend:request': (friendship: unknown) => void;
  'friend:accepted': (friendship: unknown) => void;
  'friend:rejected': (friendship: unknown) => void;
  'friend:cancelled': (friendship: unknown) => void;

  // ── Calls (server → client) ──────────────────────────────────────────────
  'call:incoming': (data: { call: Call }) => void;
  'call:state': (data: { call: Call }) => void;
  'call:offer': (data: { callId: string; fromUserId: string; sdp: string }) => void;
  'call:answer': (data: { callId: string; fromUserId: string; sdp: string }) => void;
  'call:ice-candidate': (data: { callId: string; fromUserId: string; candidate: unknown }) => void;
  'call:error': (data: { callId?: string; code: string; message: string }) => void;

  'error': (data: { message: string; code: string }) => void;
}

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
export const SOCKET_CONNECTED_EVENT = 'swipa:socket-connected';

let socket: AppSocket | null = null;
let shouldMaintainConnection = false;
let recoveryListenersWired = false;
let deferredRecoveryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Ask Socket.IO to (re)open the connection without replacing the singleton.
 *
 * `socket.connect()` is idempotent while a Manager attempt is already in
 * progress. Calling it from browser lifecycle events is therefore safe and,
 * importantly, restarts a Manager that had previously exhausted/aborted its
 * reconnect loop.
 */
export function ensureSocketConnected(): boolean {
  const current = socket;
  if (!shouldMaintainConnection || !current || current.connected) return false;
  current.connect();
  return true;
}

function requestLifecycleRecovery() {
  ensureSocketConnected();

  // WebKit can dispatch `visibilitychange` before it has fully resumed the
  // suspended Engine.IO timers. A second, bounded attempt catches the close
  // event that may be delivered immediately after the page becomes visible.
  if (deferredRecoveryTimer) clearTimeout(deferredRecoveryTimer);
  deferredRecoveryTimer = setTimeout(() => {
    deferredRecoveryTimer = null;
    ensureSocketConnected();
  }, 750);
}

function wireLifecycleRecovery() {
  if (recoveryListenersWired || typeof window === 'undefined' || typeof document === 'undefined') return;
  recoveryListenersWired = true;

  window.addEventListener('online', requestLifecycleRecovery);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') requestLifecycleRecovery();
  });
}

export function getSocket(): AppSocket {
  if (!socket) {
    // Derive socket.io path from Vite's base URL
    const base = import.meta.env.BASE_URL ?? '/';
    const socketPath = base.replace(/\/$/, '') + '/socket.io';
    socket = io(import.meta.env.VITE_WS_URL ?? '', {
      path: socketPath,
      autoConnect: false,
      transports: ['websocket', 'polling'],
      // Some mobile/carrier proxies block WebSocket upgrades while ordinary
      // HTTPS long-polling still works. Try the second transport in the same
      // connection cycle instead of waiting for another reconnect round.
      tryAllTransports: true,
      reconnection: true,
      // A short provider/route outage must not leave a foreground PWA deaf
      // until the next reload. Back off to one attempt per ~30s, but never
      // permanently give up while the authenticated app is open.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000,
      randomizationFactor: 0.5,
      timeout: 15_000,
    });
    socket.on('connect', () => {
      if (typeof window !== 'undefined') window.dispatchEvent(new Event(SOCKET_CONNECTED_EVENT));
    });
    wireLifecycleRecovery();
  }
  return socket;
}

export function connectSocket(token: string): AppSocket {
  const s = getSocket();
  s.auth = { token };
  shouldMaintainConnection = true;
  if (!s.connected) s.connect();
  return s;
}

export function disconnectSocket(): void {
  shouldMaintainConnection = false;
  socket?.disconnect();
  socket = null;
}
